/* Wechsel zwischen Spielerprofilen (vorheriger / nächster Spieler) — reine Funktionen, kein DOM.
 *
 * Der Listenkontext ist die Liste der Spieler-IDs in der Reihenfolge, wie die Liste sie beim Öffnen des
 * Profils tatsächlich zeigt (Seite, Filter, Suche, Sortierung, Abschnitte nacheinander). Er wird beim
 * Öffnen eingefroren (makeContext) und bleibt, solange das Profil offen ist. Ohne Kontext (Benachrichtigung,
 * Direktöffnung) gibt es keinen Wechsel.
 *
 * Kein Endlos-Umlauf: Am ersten Spieler gibt es kein "zurück", am letzten kein "weiter".
 * Spieler, die nicht (mehr) geladen sind (gelöscht, Rechte), werden übersprungen — `isAvailable(id)`.
 */

import { FLICK_SPEED, FLICK_MIN_PX, SWIPE_WIDTH_RATIO, EDGE_RESISTANCE } from './squadPages.js';

/** IDs aller Abschnitte nacheinander (Form der Listen: [{ key, members: [player] }]). */
export function flattenSections(sections) {
  const ids = [];
  for (const s of sections || []) for (const p of s.members || []) ids.push(p.id);
  return ids;
}

/** Eingefrorene Kopie ohne Doppelte und ohne leere Einträge. Weniger als 2 Spieler → null (kein Wechsel möglich). */
export function makeContext(ids) {
  const list = [...new Set((ids || []).filter((id) => id !== null && id !== undefined && id !== ''))];
  return list.length >= 2 ? Object.freeze(list) : null;
}

const alwaysAvailable = () => true;

/**
 * Ziel eines Schritts (delta -1 / +1) ab dem aktuellen Spieler.
 * @returns {{ id: *, skipped: number } | null} null = am Ende der Liste (oder ohne Kontext / Spieler nicht im Kontext)
 */
export function resolveStep(ids, currentId, delta, isAvailable = alwaysAvailable) {
  if (!ids || !ids.length || (delta !== -1 && delta !== 1)) return null;
  const at = ids.indexOf(currentId);
  if (at < 0) return null;
  let skipped = 0;
  for (let i = at + delta; i >= 0 && i < ids.length; i += delta) {
    if (isAvailable(ids[i])) return { id: ids[i], skipped };
    skipped += 1;
  }
  return null;
}

/** Darf man in Richtung delta wechseln? (Sperre an den Enden.) */
export function canStepProfile(ids, currentId, delta, isAvailable = alwaysAvailable) {
  return resolveStep(ids, currentId, delta, isAvailable) !== null;
}

/**
 * Anzeige "7 / 18": Platz des aktuellen Spielers unter den verfügbaren und deren Anzahl.
 * @returns {{ index: number, total: number } | null} index ab 1; null, wenn kein Wechsel möglich ist
 */
export function positionOf(ids, currentId, isAvailable = alwaysAvailable) {
  if (!ids || !ids.length) return null;
  const available = ids.filter((id) => id === currentId || isAvailable(id));
  const at = available.indexOf(currentId);
  if (at < 0 || available.length < 2) return null;
  return { index: at + 1, total: available.length };
}

/* ---------- Wischen ---------- */

/** Tags, in denen eine Geste nie als Seitenwechsel zählt (Eingabe, Medien-Bedienung). */
const BLOCKING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'VIDEO', 'AUDIO', 'CANVAS']);

/**
 * Beginnt die Geste in einem Element, das sie selbst braucht? Läuft vom Ziel bis `boundary` (ausgeschlossen)
 * nach oben. Gesperrt bei: Eingabefeld, Medienelement, editierbarem Inhalt, `data-no-swipe`, oder einem
 * Element mit eigenem waagerechten Scrollen (overflow-x auto/scroll UND Inhalt breiter als das Element).
 * `getStyle(el)` liefert den berechneten Stil (im Browser getComputedStyle) — so bleibt die Funktion DOM-frei.
 */
export function isSwipeBlocked(target, boundary, getStyle) {
  for (let el = target; el && el !== boundary; el = el.parentElement || el.parentNode) {
    if (el.nodeType === 3) continue;
    const tag = String(el.tagName || '').toUpperCase();
    if (BLOCKING_TAGS.has(tag)) return true;
    if (el.isContentEditable || el.contentEditable === 'true') return true;
    if (el.dataset && el.dataset.noSwipe !== undefined) return true;
    if (el.scrollWidth > el.clientWidth && getStyle) {
      const ox = getStyle(el)?.overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
  }
  return false;
}

/** Ist das Ziel eines Tastendrucks ein Feld, in dem Pfeiltasten dem Cursor gehören? */
export function isTypingTarget(target) {
  if (!target) return false;
  const tag = String(target.tagName || '').toUpperCase();
  return BLOCKING_TAGS.has(tag) || tag === 'OPTION' || target.isContentEditable === true;
}

/**
 * Beim Loslassen: -1 (vorheriger), +1 (nächster) oder 0 (bleiben).
 * Wie settleTarget (domain/squadPages.js): Schneller kurzer Wisch (Flick) geht eine Stufe in Wischrichtung,
 * sonst zählt, ob mehr als die halbe Strecke einer Seite gewischt wurde (Breite × SWIPE_WIDTH_RATIO).
 * dx negativ = nach links = nächster Spieler. Die Sperre an den Enden gilt in jedem Fall.
 */
export function swipeStepDelta({ dx, dt, width, canPrev, canNext }) {
  const speed = dt > 0 ? Math.abs(dx) / dt : 0;
  const flick = speed >= FLICK_SPEED && Math.abs(dx) >= FLICK_MIN_PX;
  const progress = -dx / (Math.max(width, 1) * SWIPE_WIDTH_RATIO);
  const raw = flick ? Math.sign(-dx) : Math.round(progress);
  const delta = Math.max(-1, Math.min(1, raw));
  if (delta === 1 && !canNext) return 0;
  if (delta === -1 && !canPrev) return 0;
  return delta;
}

/** Versatz des Inhalts beim Ziehen: frei bis zu einem Anteil der Breite; ohne Ziel in dieser Richtung nur mit Gegenzug. */
export function dragOffset({ dx, width, canPrev, canNext }) {
  const limit = Math.max(width, 1) * 0.35;
  const allowed = dx < 0 ? canNext : canPrev;
  const factor = allowed ? 1 : EDGE_RESISTANCE;
  return Math.max(-limit, Math.min(limit, dx * factor));
}
